<template>
  <PaginaAdministrar
    titulo="Demanda de Financiamento"
    subtitulo="Pedidos de venda que exigem operação financeira"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="false"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-3">
          <label class="form-label">Estado da Pendência</label>
          <select class="form-select" v-model="filtros.estado" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="e in listas.estadosDemanda" :key="e.idListaClienteItem" :value="e.idListaClienteItem">
              {{ e.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-md-3">
          <CampoBusca
            id="filtroCliente"
            label="Cliente"
            v-model="filtros.idCliente"
            :configuracao-busca="parceiroSearch"
            @selecionado="val => filtros.nomeCliente = val?.nomeFantasia || val?.razaoSocial"
          />
        </div>
        <div class="col-md-2">
          <label class="form-label">Moeda</label>
          <select class="form-select" v-model="filtros.moeda" @change="aplicarFiltros">
            <option value="">Todas</option>
            <option v-for="m in listas.moedas" :key="m.idListaClienteItem" :value="m.idListaClienteItem">
              {{ m.textoLista }}
            </option>
          </select>
        </div>
        <div class="col-md-2">
          <label class="form-label">Emissão De</label>
          <input class="form-control" type="date" v-model="filtros.dataInicio" @change="aplicarFiltros" />
        </div>
        <div class="col-md-2">
          <label class="form-label">Emissão Até</label>
          <input class="form-control" type="date" v-model="filtros.dataFim" @change="aplicarFiltros" />
        </div>
        <div class="col-md-2">
          <label class="form-label">Nº Pedido</label>
          <input class="form-control" v-model="filtros.numeroPedido" placeholder="Nº pedido..." @keyup.enter="aplicarFiltros" />
        </div>
        <div class="col-md-12 text-end">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhuma demanda de financiamento encontrada."
      row-key="idPedidoVenda"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
    >
      <template #numeroPedidoVenda="{ row }">
        <span class="fw-medium text-body-emphasis">{{ row.numeroPedidoVenda || '-' }}</span>
      </template>

      <template #nomeCliente="{ row }">
        <span>{{ row.nomeCliente || '-' }}</span>
      </template>

      <template #valorTotalNegociado="{ row }">
        <span class="fw-medium">{{ formatarValorMoeda(row.valorTotalNegociado, row.lstMoeda) }}</span>
      </template>

      <template #lstMoeda="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle">
          {{ obterTextoMoeda(row.lstMoeda) }}
        </span>
      </template>

      <template #lstEstadoDemanda="{ row }">
        <span class="badge" :class="obterClasseEstado(row.lstEstadoDemanda)">
          {{ obterTextoEstado(row.lstEstadoDemanda) }}
        </span>
      </template>

      <template #instituicao="{ row }">
        <span class="small text-muted">{{ row.nomeInstituicaoFinanceira || '-' }}</span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper">
          <button
            v-if="row.lstEstadoDemanda === FIN_FINANCIAMENTO_DEMANDA_ST.AguardandoCriacao"
            v-show="temPermissaoEscrita"
            class="btn btn-sm btn-primary"
            title="Criar financiamento"
            @click="criarFinanciamento(row)"
          >
            <i class="bi bi-plus-circle me-1"></i> Criar
          </button>
          <button
            v-if="row.lstEstadoDemanda === FIN_FINANCIAMENTO_DEMANDA_ST.RascunhoAFormalizar"
            v-show="temPermissaoEscrita"
            class="btn btn-sm btn-primary"
            title="Editar rascunho"
            @click="editarFinanciamento(row)"
          >
            <i class="bi bi-pencil-fill me-1"></i> Editar
          </button>
          <button
            v-if="row.lstEstadoDemanda === FIN_FINANCIAMENTO_DEMANDA_ST.TitulosSemVinculo"
            v-show="temPermissaoEscrita"
            class="btn btn-sm btn-warning"
            title="Vincular t\u00edtulos"
            @click="vincularTitulos(row)"
          >
            <i class="bi bi-link-45deg me-1"></i> Vincular
          </button>
          <button
            v-if="row.lstEstadoDemanda === FIN_FINANCIAMENTO_DEMANDA_ST.EmAndamento"
            class="btn btn-sm btn-outline-info"
            title="Ver financiamento"
            @click="verFinanciamento(row)"
          >
            <i class="bi bi-eye-fill me-1"></i> Ver
          </button>
          <button
            class="btn btn-sm btn-outline-secondary"
            title="Ver pedido"
            @click="verPedido(row)"
          >
            <i class="bi bi-receipt"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useMetadataListasStore } from '@/stores/metadataListas'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import { parceiroSearch } from '@/search-definitions/parceiroSearch'
import { FIN_FINANCIAMENTO_DEMANDA_ST } from '@/constants/FIN_FINANCIAMENTO_DEMANDA_ST'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const authStore = useAuthStore()

const temPermissaoEscrita = computed(() =>
  authStore.isAdminInterno || authStore.isAdminCliente || authStore.pode('FINANCEIRO_FINANCIAMENTO_DEMANDA_COMPLETO')
)
const metadataStore = useMetadataListasStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao(25)

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const listas = reactive({
  estadosDemanda: [],
  moedas: []
})

const filtros = reactive({
  estado: '',
  moeda: '',
  dataInicio: '',
  dataFim: '',
  numeroPedido: '',
  idCliente: '',
  nomeCliente: ''
})

const colunas = [
  { field: 'numeroPedidoVenda', label: 'Pedido', width: 150, sortable: true, slotName: 'numeroPedidoVenda' },
  { field: 'nomeCliente', label: 'Cliente', width: 200, sortable: true, slotName: 'nomeCliente' },
  { field: 'valorTotalNegociado', label: 'Valor', width: 130, align: 'right', slotName: 'valorTotalNegociado' },
  { field: 'lstMoeda', label: 'Moeda', width: 80, align: 'center', slotName: 'lstMoeda' },
  { field: 'lstEstadoDemanda', label: 'Estado', width: 140, slotName: 'lstEstadoDemanda' },
  { field: 'nomeInstituicaoFinanceira', label: 'Instituição', slotName: 'instituicao' },
  { field: 'acoes', label: 'Ações', width: 230, align: 'right', slotName: 'acoes', resizable: false, sortable: false }
]

const sortResolver = (field, order, data) => {
  if (field === 'nomeCliente') {
    return [...data].sort((a, b) => {
      const textoA = (a.nomeCliente || '').toLowerCase()
      const textoB = (b.nomeCliente || '').toLowerCase()
      const cmp = textoA.localeCompare(textoB, 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }
  if (field === 'lstEstadoDemanda') {
    return [...data].sort((a, b) => {
      const textoA = obterTextoEstado(a.lstEstadoDemanda)
      const textoB = obterTextoEstado(b.lstEstadoDemanda)
      const cmp = textoA.localeCompare(textoB, 'pt-BR')
      return order === 'asc' ? cmp : -cmp
    })
  }
  return null
}

const handleSortChange = () => { buscarDados(1) }

watch(() => authStore.empresaAtiva, async (novaEmpresa) => {
  if (novaEmpresa) {
    await carregarListas()
    buscarDados(1)
  } else {
    lista.value = []
    paginacao.value.TotalCount = 0
    listas.estadosDemanda = []
    listas.moedas = []
  }
})

onMounted(async () => {
  await carregarListas()
  buscarDados(1)
})

onActivated(() => {
  buscarDados(paginacao.value.CurrentPage)
})

const obterClasseEstado = (estado) => {
  switch (estado) {
    case FIN_FINANCIAMENTO_DEMANDA_ST.AguardandoCriacao: return 'badge-ghost-warning'
    case FIN_FINANCIAMENTO_DEMANDA_ST.RascunhoAFormalizar: return 'badge-ghost-secondary'
    case FIN_FINANCIAMENTO_DEMANDA_ST.AguardandoFaturamento: return 'badge-ghost-info'
    case FIN_FINANCIAMENTO_DEMANDA_ST.TitulosSemVinculo: return 'badge-ghost-danger'
    case FIN_FINANCIAMENTO_DEMANDA_ST.EmAndamento: return 'badge-ghost-success'
    default: return 'badge-ghost-info'
  }
}

const obterTextoEstado = (estado) => {
  switch (estado) {
    case FIN_FINANCIAMENTO_DEMANDA_ST.AguardandoCriacao: return 'Aguardando Criação'
    case FIN_FINANCIAMENTO_DEMANDA_ST.RascunhoAFormalizar: return 'Rascunho a Formalizar'
    case FIN_FINANCIAMENTO_DEMANDA_ST.AguardandoFaturamento: return 'Aguardando Faturamento'
    case FIN_FINANCIAMENTO_DEMANDA_ST.TitulosSemVinculo: return 'Títulos Sem Vínculo'
    case FIN_FINANCIAMENTO_DEMANDA_ST.EmAndamento: return 'Em Andamento'
    default: return estado || '-'
  }
}

const obterTextoMoeda = (codigoMoeda) => {
  if (!codigoMoeda) return '-'
  const moeda = listas.moedas.find(m => String(m.idListaClienteItem) === String(codigoMoeda))
  return moeda?.textoLista || codigoMoeda
}

const formatarValorMoeda = (valor, codigoMoeda) => {
  if (valor == null) return '-'
  const sigla = obterTextoMoeda(codigoMoeda)
  try {
    const valorFormatado = new Intl.NumberFormat('pt-BR', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
    return sigla !== '-' ? `${valorFormatado} ${sigla}` : valorFormatado
  } catch (e) {
    return `${valor?.toFixed(2) ?? '-'} ${sigla !== '-' ? sigla : ''}`.trim()
  }
}

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['FIN_FINANCIAMENTO_DEMANDA_ST', 'MOE'])
    listas.estadosDemanda = result.FIN_FINANCIAMENTO_DEMANDA_ST || []
    listas.moedas = result.MOE || []
  } catch (e) {
    console.warn('Erro ao carregar listas da demanda:', e)
  }
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)
    if (filtros.estado) params.append('estado', filtros.estado)
    if (filtros.moeda) params.append('lstMoeda', filtros.moeda)
    if (filtros.dataInicio) params.append('dataEmissaoInicio', filtros.dataInicio)
    if (filtros.dataFim) params.append('dataEmissaoFim', filtros.dataFim)
    if (filtros.numeroPedido) params.append('numeroPedidoVenda', filtros.numeroPedido)
    if (filtros.idCliente) params.append('idParceiroNegocioCliente', filtros.idCliente)

    const response = await api.get(`/FinanciamentoDemanda?${params.toString()}`)
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    const data = error.response?.data
    let msg = 'Não foi possível carregar os dados.'
    if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }
    erro.value = msg
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => buscarDados(1)

const limparFiltros = () => {
  filtros.estado = ''
  filtros.moeda = ''
  filtros.dataInicio = ''
  filtros.dataFim = ''
  filtros.numeroPedido = ''
  filtros.idCliente = ''
  filtros.nomeCliente = ''
  buscarDados(1)
}

const mudarPagina = (page) => buscarDados(page)

const mudarTamanhoPagina = (size) => {
  paginacao.PageSize = size
  buscarDados(1)
}

const criarFinanciamento = (row) => {
  emit('adicionar-item', { idPedidoVenda: row.idPedidoVenda })
}

const editarFinanciamento = (row) => {
  emit('editar-item', { idOperacaoFinanciamento: row.idOperacaoFinanciamento })
}

const vincularTitulos = (row) => {
  if (row.idOperacaoFinanciamento) {
    emit('editar-item', { idOperacaoFinanciamento: row.idOperacaoFinanciamento })
  } else {
    emit('adicionar-item', { idPedidoVenda: row.idPedidoVenda })
  }
}

const verFinanciamento = (row) => {
  emit('editar-item', { idOperacaoFinanciamento: row.idOperacaoFinanciamento })
}

const verPedido = (row) => {
  // Navega para o PedidoVenda em modo leitura via showModule
  emit('editar-item', { idPedidoVenda: row.idPedidoVenda, _isDemandaVerPedido: true })
}

defineExpose({ buscarDados })
</script>
